import {
  Alert,
  Flex,
  Grid,
  Heading,
  IconStudentViewLine,
  List,
  Text,
} from "@instructure/ui";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { useSearchParams } from "react-router";
import {
  ERROR,
  useNotification,
} from "../../../components/CustomHooks/NotificationProvider";
import { fetchHelperTextForTemplate } from "../../../components/Reflection/utils";
import { ConfigService } from "../../../services/ConfigService";
import { OfferingService } from "../../../services/OfferingService";
import { ReflectionTemplateService } from "../../../services/ReflectionTemplateService";
import { ReviewService } from "../../../services/ReviewService";
import { SectionService } from "../../../services/SectionService";
import { FeatureContext, UserDetailContext } from "../../../utils/Context";
import { default as colors } from "../../../utils/CourseColors";
import CourseReviewCycleDetails from "./Components/CourseReviewCycleDetails";
import AssignmentsToggle from "./Components/SectionCard/AssignmentsToggle";
import { SectionCard } from "./Components/SectionCard/SectionCard";
import SectionHeader from "./Components/SectionCard/SectionHeader";
import AssignmentView from "./Views/AssignmentView";
import CourseView from "./Views/CourseView";

const FacultyDashboard = ({ trigger, setTrigger, subjects }) => {
  const { t } = useTranslation();
  const userDetails = useContext(UserDetailContext);
  const features = useContext(FeatureContext);
  const showNotification = useNotification();
  const [searchParams] = useSearchParams();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("course");
  const [selectedAssignment, setSelectedAssignment] = useState({});
  const [chartLoad, setChartLoad] = useState(false);
  const selectedCourseRef = useRef(null);

  const finalizeFlag =
    features.find((f) => f.name === "finalize_results_flag")?.value === "TRUE";

  const [facDashboardFilters, setFacDashboardFilters] = useState({
    subject: "",
    LOType: "CSLO",
    programs: [],
    categories: [],
    terms: [],
    masteryResults: "true",
    cycle: {},
    courseLevels: [],
    courses: [],
    type: [],
    variety: ["outcome"],
    offerings: [],
  });
  const [noSections, setNoSections] = useState(false);
  const [reflectionTemplates, setReflectionTemplates] = useState([]);
  const [sectionRefTemplate, setSectionRefTemplate] = useState(null);
  const [courseReviewCycles, setCourseReviewCycles] = useState([]);
  const [defaultDeadline, setDefaultDeadline] = useState(null);

  const courseIdFilter = searchParams.get("courseId");

  useEffect(() => {
    if (!loading && courseIdFilter && selectedCourseRef?.current) {
      selectedCourseRef.current.ref.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
    }
  }, [loading, courseIdFilter]);

  useEffect(() => {
    const fetchSectionInfo = async () => {
      try {
        const [
          offerings,
          facultySections,
          reflectionTemplates,
          reviewCycles,
          defaultDeadline,
        ] = await Promise.all([
          OfferingService.getOfferingsByFacultyID(userDetails.canvas_id),
          SectionService.getSectionsByFacultyID(userDetails.canvas_id),
          ReflectionTemplateService.getTemplates(),
          userDetails.isFaculty() ? [] : ReviewService.getCycles(),
          ConfigService.getGlobalDaysAfterTermDeadline(),
        ]);
        setDefaultDeadline(defaultDeadline);
        const canvasSections = facultySections?.filter(
          (section) => section.canvas_id !== -1
        );

        if (canvasSections && canvasSections.length > 0) {
          setReflectionTemplates(reflectionTemplates);
          setCourseReviewCycles(reviewCycles);
          let selectedSection = canvasSections[0];
          canvasSections.forEach((section) => {
            const offering = offerings.find(
              (offering) => offering.id === section.offering.id
            );
            if (offering) {
              section.course_id = offering.course_summary.parent_course_id;
              section.course_code = offering.course_summary.code;
              section.learning_outcomes =
                offering && Object.hasOwn(offering, "learning_outcomes")
                  ? offering.learning_outcomes.filter(
                      (lo) => !Object.hasOwn(lo, "unapproved")
                    )
                  : [];
              section.unapproved_outcomes =
                offering && Object.hasOwn(offering, "learning_outcomes")
                  ? offering.learning_outcomes.filter(
                      (lo) => Object.hasOwn(lo, "unapproved") && lo.unapproved
                    )
                  : [];
              section.current = false;
              if (section.name) {
                section.name = section.name.replace(/,/g, ", ");
              }
              // values are of a different type (int and str) hence the loose equality operator (==) is intentional
              if (section.canvas_id == courseIdFilter) {
                selectedSection = section;
              }
            }
          });
          selectedSection.current = true;
          fetchSectionReflectionTemplate(
            selectedSection,
            reflectionTemplates,
            reviewCycles
          );
          setSections(canvasSections);
          setFacDashboardFilters((prevFilters) => ({
            ...prevFilters,
            subject: selectedSection.offering?.subject
              ? subjects.find(
                  (s) => s.name === selectedSection.offering.subject
                )?.title
              : t("dashboard.noDepartmentAvailable"),
            courses: [], // default to populating the offering results on fac dashboard
            offerings: selectedSection.offering?.id
              ? [selectedSection.offering.id]
              : [],
            LOType: "CSLO",
            masteryResults: "true",
          }));

          setLoading(false);
        } else {
          setNoSections(true);
        }
      } catch {
        showNotification(
          t("notifications.general.fetchError", { obj: "Course Information" }),
          ERROR
        );
      }
    };
    if (userDetails.name && subjects.length > 0) {
      fetchSectionInfo();
    }
  }, [userDetails, subjects, courseIdFilter, showNotification]);

  const detailTitle = () => {
    const currentSection = sections.find((c) => c.current);
    let title = t("dashboard.detailsFor") + ": ";
    title +=
      currentSection.name ??
      currentSection.offering.code + ", " + currentSection.term.name;

    if (selectedAssignment.assignment) {
      title +=
        " > " +
        (selectedAssignment.assignment.course_id === -1
          ? t("assessment.externalAssessment")
          : selectedAssignment.assignment.name);
    }

    return title;
  };

  function changeCurrentSection(section) {
    fetchSectionReflectionTemplate(
      section,
      reflectionTemplates,
      courseReviewCycles
    );
    setSections((prevSections) =>
      prevSections.map((sec) => ({ ...sec, current: sec.id === section.id }))
    );
  }

  async function fetchSectionReflectionTemplate(
    section,
    templates,
    courseReviewCycles
  ) {
    let selectedReflectionTemplate;

    const associatedCycle = courseReviewCycles.find((cycle) =>
      cycle.reviewables.some(
        (c) => c.ddl_id === section.offering.parent_course_id
      )
    );
    const associatedTemplate = templates.find(
      (t) => t.id === associatedCycle?.reflection_template_id
    );
    if (associatedTemplate) {
      selectedReflectionTemplate = associatedTemplate;
    } else {
      const sectionDept = section.offering.subject;
      const filteredTemplates = templates.filter((template) =>
        template.departments.some((dept) => dept.name === sectionDept)
      );
      const associatedTemplate = filteredTemplates.find(
        (template) =>
          new Date(section.term.start_at) >=
            new Date(template.start_year.start_at) &&
          new Date(section.term.end_at) <= new Date(template.end_year.end_at)
      );
      if (associatedTemplate) {
        selectedReflectionTemplate = associatedTemplate;
      }
    }

    if (selectedReflectionTemplate) {
      const helperTexts = await fetchHelperTextForTemplate(
        selectedReflectionTemplate.sections
      );
      helperTexts.forEach((helperText, i) => {
        selectedReflectionTemplate.sections[i].helper_text = helperText.content;
      });
      setSectionRefTemplate(selectedReflectionTemplate);
    } else {
      setSectionRefTemplate(null);
    }
  }

  function handleSectionSelect(section) {
    if (!section.current || Object.keys(selectedAssignment).length > 0) {
      changeCurrentSection(section);
      setSelectedAssignment({});
      setView("course");
      setChartLoad(true);
      setTrigger(true);
      setFacDashboardFilters((prevFilters) => ({
        ...prevFilters,
        subject: section.offering?.subject
          ? subjects.find((s) => s.name === section.offering.subject)?.title
          : t("dashboard.noDepartmentAvailable"),
        courses: [], // default to offering results
        offerings: section.offering?.id ?? [],
        LOType: "CSLO",
        masteryResults: "true",
        variety: ["outcome"],
      }));
    }
  }

  function handleAssignmentSelect(assignment, section) {
    if (!section.current) {
      changeCurrentSection(section);
    }
    if (assignment.assignment.id !== selectedAssignment?.assignment?.id) {
      setFacDashboardFilters((prevFilters) => ({
        ...prevFilters,
        subject: section.offering?.subject
          ? subjects.find((s) => s.name === section.offering.subject)?.title
          : t("dashboard.noDepartmentAvailable"),
        courses: [], // default to offering results
        offerings: section.offering?.id ?? [],
        LOType: "CSLO",
        masteryResults: "true",
        variety: ["outcome"],
      }));
      setSelectedAssignment(assignment);
      setView("assignment");
      setChartLoad(true);
      setTrigger(true);
    }
  }

  function associatedCycle(section) {
    return courseReviewCycles.find((cycle) =>
      cycle.reviewables.some(
        (c) => c.ddl_id === section.offering.parent_course_id
      )
    );
  }

  return (
    <>
      <Grid startAt="large">
        <Grid.Row>
          <Grid.Col width={3}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                maxHeight: "calc(100vh - 3rem)",
              }}
            >
              <Heading as="h2" level="h3" border="bottom">
                {t("navigation.myCourses")}
              </Heading>
              <Flex wrap="wrap" margin="small 0 0 0">
                <Flex.Item size="100%">
                  <IconStudentViewLine />{" "}
                  <Text weight="bold">: {t("dashboard.currentView")}</Text>
                </Flex.Item>
              </Flex>
              <div style={{ overflowY: "auto", padding: "0 15px 0 5px" }}>
                {!noSections &&
                  (!loading ? (
                    <List isUnstyled>
                      {sections.map((section, index) => (
                        <List.Item
                          margin="small 0"
                          key={index}
                          ref={section.current ? selectedCourseRef : null}
                        >
                          <SectionCard
                            data-testid={`${section.course_code}-${section.term.name}`}
                            $current={section.current}
                            color={colors[index]}
                          >
                            <Flex wrap="wrap">
                              <Flex.Item size="100%">
                                <SectionHeader
                                  section={section}
                                  cardColor={colors[index]}
                                  handleSectionSelect={handleSectionSelect}
                                  reflectionTemplates={reflectionTemplates}
                                  sectionRefTemplate={sectionRefTemplate}
                                  defaultDeadline={defaultDeadline}
                                />
                              </Flex.Item>
                              <Flex.Item size="100%" margin="x-small 0 0 0">
                                <AssignmentsToggle
                                  section={section}
                                  selectedAssignment={selectedAssignment}
                                  handleSectionSelect={handleSectionSelect}
                                  handleAssignmentSelect={
                                    handleAssignmentSelect
                                  }
                                  setChartLoad={setChartLoad}
                                  setSections={setSections}
                                />
                              </Flex.Item>
                            </Flex>
                          </SectionCard>
                        </List.Item>
                      ))}
                    </List>
                  ) : (
                    <Skeleton
                      height={128}
                      count={5}
                      style={{ margin: "0.5rem 0" }}
                    />
                  ))}
              </div>
            </div>
          </Grid.Col>
          <Grid.Col>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: "calc(100vh - 3rem)",
              }}
            >
              <Heading as="h1" level="h3" border="bottom">
                {noSections
                  ? t("dashboard.noDataToDisplay")
                  : !loading
                    ? detailTitle()
                    : t("global.loading") + "..."}
              </Heading>
              <div
                style={{
                  overflowY: "auto",
                  padding: "0.75rem 0.375rem 0.375rem 0.375rem",
                  flexGrow: 1,
                }}
              >
                {finalizeFlag && sections.length > 0 && !noSections && (
                  <CourseReviewCycleDetails
                    key={sections.find((c) => c.current).id}
                    section={sections.find((c) => c.current)}
                    cycle={associatedCycle(sections.find((c) => c.current))}
                  />
                )}
                {noSections ? (
                  <Alert variant="info">{t("dashboard.noSections")}</Alert>
                ) : !loading ? (
                  view === "course" ? (
                    <CourseView
                      currentSection={sections.find((c) => c.current)}
                      reflectionTemplates={reflectionTemplates}
                      sectionRefTemplate={sectionRefTemplate}
                      dashboardFilters={facDashboardFilters}
                      setDashboardFilters={setFacDashboardFilters}
                      trigger={trigger}
                      setTrigger={setTrigger}
                      chartLoad={chartLoad}
                      setChartLoad={setChartLoad}
                    />
                  ) : (
                    <AssignmentView
                      currentSection={sections.find((c) => c.current)}
                      reflectionTemplates={reflectionTemplates}
                      sectionRefTemplate={sectionRefTemplate}
                      dashboardFilters={facDashboardFilters}
                      setDashboardFilters={setFacDashboardFilters}
                      trigger={trigger}
                      setTrigger={setTrigger}
                      selectedAssignment={selectedAssignment}
                      chartLoad={chartLoad}
                      setChartLoad={setChartLoad}
                    />
                  )
                ) : (
                  <Skeleton
                    height={330}
                    count={2}
                    style={{ margin: "10px 0" }}
                  />
                )}
              </div>
            </div>
          </Grid.Col>
        </Grid.Row>
      </Grid>
    </>
  );
};

export default FacultyDashboard;
